import { useMediaQuery } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalViews } from '../../../constants';
import { StockCountItem, StockStatusType } from '../../../hooks';
import { Typography } from '../../../stories/atoms';
import { ColumnFlexContainer, ContainerV2 } from '../../../stories/atoms/containerV2/ContainerV2';
import { muiTheme } from '../../../theme';
import { yearlyStockTakeTranslations } from '../../../translations';
import { StockDetailsCard } from '../stockDetailsCard/StockDetailsCard';
import { StyledPickContainer } from '../YearlyStockTakePage.styles';

type MultipleStockViewProps = {
  modalView: ModalViews;
  pickedItems: StockCountItem[];
  unpickedItems: StockCountItem[];
  removeItem?: (item: StockCountItem, status: StockStatusType) => void;
};

export const MultipleStockView: FC<MultipleStockViewProps> = ({
  modalView,
  pickedItems,
  unpickedItems,
  removeItem = () => {},
}) => {
  const { t } = useTranslation();
  const { pickUnpickAction: pickUnpickActionTranslations } = yearlyStockTakeTranslations;
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  return (
    <ContainerV2
      display="flex"
      flexDirection={isMobile ? 'column' : 'row'}
      gap={[4]}
      data-testid="multiple-stock-view"
    >
      {!pickedItems.length && !unpickedItems.length && (
        <StyledPickContainer
          minHeight={[62]}
          maxHeight={[62]}
          padding={[2]}
          borderRadius={[2]}
          hasBorder
          overflow="auto"
          justifyContent="center"
          alignItems="center"
          flex={1}
          data-testid="no-items"
        >
          <Typography type="body3">
            {t(
              pickUnpickActionTranslations.noItemSelected.key,
              pickUnpickActionTranslations.noItemSelected.defaultValue,
            )}
          </Typography>
        </StyledPickContainer>
      )}

      {!!pickedItems.length && (
        <ColumnFlexContainer flex={1} data-testid="picked-items">
          <Typography type="body3" margin={[0, 0, 2]}>
            {t(pickUnpickActionTranslations.pickItems.key, pickUnpickActionTranslations.pickItems.defaultValue)} (
            {pickedItems.length})
          </Typography>
          <StyledPickContainer
            minHeight={[62]}
            maxHeight={[62]}
            padding={[2]}
            borderRadius={[2]}
            hasBorder
            overflow="auto"
          >
            {pickedItems.map((item) => (
              <StockDetailsCard
                key={item.sicScope}
                isBulkAction
                stockCountItem={item}
                modalView={modalView}
                removeItem={() => removeItem(item, 'Picked')}
              />
            ))}
          </StyledPickContainer>
        </ColumnFlexContainer>
      )}
      {!!unpickedItems.length && (
        <ColumnFlexContainer flex={1} data-testid="unpicked-items">
          <Typography type="body3" margin={[0, 0, 2]}>
            {t(
              pickUnpickActionTranslations.unpickItems.key,
              pickUnpickActionTranslations.unpickItems.defaultValue,
            )}{' '}
            ({unpickedItems.length})
          </Typography>
          <StyledPickContainer
            minHeight={[62]}
            maxHeight={[62]}
            padding={[2]}
            borderRadius={[2]}
            hasBorder
            overflow="auto"
          >
            {unpickedItems.map((item) => (
              <StockDetailsCard
                key={item.sicScope}
                isBulkAction
                stockCountItem={item}
                modalView={modalView}
                removeItem={() => removeItem(item, 'Pick')}
              />
            ))}
          </StyledPickContainer>
        </ColumnFlexContainer>
      )}
    </ContainerV2>
  );
};
